import {
  GetScriptById,
  GetScriptsByUserId,
  CreateScript
} from '@sukiai/gql/app'
import React from 'react'
import get from 'lodash/get'
import { Constants } from '@sukiai/components'
import { compose, graphql, withApollo } from 'react-apollo'
import Editor, { V2_DEFAULT_CONTENT } from '@sukiai/quill-editor'

import {
  LO_STO,
  EDITOR_TYPE,
  SESH_STO,
  sortScriptsByName,
  showSettings,
  setToast,
  isEmbedScriptsView,
  showScripts,
  isNoteView
} from '../../lib'
import {
  InvisibleContainer,
  Container,
  HeaderActions,
  Button,
  ScriptNameInput,
  Body,
  SectionOptions,
  Platter,
  SectionBlock,
  ScriptNameTip,
  AmwellInvisibleContainer,
  AmwellContainer,
  AmwellBody,
  AmwellContent,
  AmwellFooter,
  AmwellScriptHeader,
  AmwellScriptHeading,
  CloseModalButtonDark,
  AmwellScriptNameInput,
  AmwellPlatter,
  AmwellSectionOptions,
  AmwellScriptOption,
  AmwellSectionBlock,
  AmwellActionButton
} from './styled'
import {
  SET_MODAL
} from '../../apollo/actions'
import loSto from '../../config/loSto'
import seshSto from '../../config/seshSto'
import { logger } from '../../lib/logging'
import { isOrgEmrIntegrated, isAmwellTheme } from '../../lib/util'
import { withRouter } from 'react-router-dom'

const { TOAST, MODALS } = Constants

class NewScriptModal extends React.Component {
  state = {
    scriptName: '',
    blocks: [
      {
        sectionId: this.props.sections?.[0]?.id,
        content: V2_DEFAULT_CONTENT
      }
    ],
    sectionOptionsOpen: null,
    isCreatingScript: false
  };

  componentDidMount = async () => {
    document.addEventListener('mousedown', this.handleClick)
  };

  componentWillUnmount () {
    document.removeEventListener('mousedown', this.handleClick)
  }

  handleClick = (e) => {
    const { sectionOptionsOpen } = this.state
    if (!sectionOptionsOpen) {
      return
    }
    const isSectionDropDown = e.target.classList.contains('section-dropdown')
    if (!isSectionDropDown) {
      this.setState({ sectionOptionsOpen: false })
    }
  };

  componentWillReceiveProps (nextProps) {
    nextProps.shouldDismiss && this.props.open && nextProps.removeModal()
  }

  handleClose = () => {
    this.setState({ isCreatingScript: false })
    const { match, client } = this.props
    if (isEmbedScriptsView(match.url)) {
      client.mutate({
        mutation: SET_MODAL,
        variables: {
          content: {
            __typename: 'ModalContent',
            type: MODALS.AMWELL_SCRIPTS
          }
        }
      })
    } else if (isNoteView()) {
      showScripts()
    } else {
      showSettings()
    }
  };

  handleChange = (e) => {
    this.setState({ [e.target.name]: e.target.value })
  };

  onEditorChange = (newValue, sectionId) => {
    const { blocks } = this.state

    const newBlocks = blocks.slice()
    const idx = newBlocks.findIndex((s) => s.sectionId === sectionId)
    if (idx < 0) return
    newBlocks[idx].content = newValue
    this.setState({ blocks: newBlocks })
  };

  onSelectSection = (blockIdx, sectionId, e) => {
    e.stopPropagation()
    const { blocks } = this.state
    const newBlocks = [...blocks]
    newBlocks[blockIdx].sectionId = sectionId

    this.setState({
      blocks: newBlocks,
      sectionOptionsOpen: null
    })
  };

  addToCache = async (cache, { data: { createMacro } }) => {
    const organizationId = seshSto.session(SESH_STO.CURRENT_ORGANIZATION)?.id
    const userId = loSto.get(LO_STO.CURRENT_USER)?.id
    const newScript = get(createMacro, 'macro')
    const queryVariables = { organizationId, userId }
    const { macros: allScriptsRes } = cache.readQuery({
      query: GetScriptsByUserId,
      variables: queryVariables
    })

    // Add the new script to the list of scripts
    const allScripts = [...get(allScriptsRes, 'results', []), newScript]
    // Re-sort the scripts by name
    sortScriptsByName(allScripts)

    // Update the cache with the new list of scripts
    await cache.writeQuery({
      query: GetScriptsByUserId,
      variables: queryVariables,
      data: {
        macros: {
          __typename: 'QueryMacrosPayload',
          count: get(allScripts, 'length', 0),
          results: allScripts
        }
      }
    })

    // Write the data of the new script to the cache
    await cache.writeQuery({
      query: GetScriptById,
      variables: { organizationId, id: newScript.id },
      data: {
        macros: {
          __typename: 'QueryMacrosPayload',
          count: 1,
          results: [newScript]
        }
      }
    })
  };

  handleCreateScript = () => {
    const { createScript } = this.props
    const { scriptName, blocks } = this.state
    this.setState({ isCreatingScript: true })

    // the parse and stringify helps flatten out the newlines and \r
    const emptyBlocks = blocks.some((block) => {
      const a = JSON.stringify(JSON.parse(block.content))
      const b = JSON.stringify(JSON.parse(V2_DEFAULT_CONTENT))
      return a === b
    })
    if (emptyBlocks) {
      setToast(
        TOAST.TYPE.WARNING,
        'Please enter some content for your script',
        TOAST.DURATION.MEDIUM
      )
      this.setState({ isCreatingScript: false })
      return
    }

    createScript({
      variables: {
        organizationId: seshSto.session(SESH_STO.CURRENT_ORGANIZATION)?.id,
        userId: loSto.get(LO_STO.CURRENT_USER)?.id,
        name: scriptName,
        blocks: blocks
      },
      update: (cache, response) => {
        this.addToCache(cache, response)
      }
    })
      .then(() => {
        this.setState({ isCreatingScript: false })
        this.handleClose()
      })
      .catch((err) => {
        this.setState({ isCreatingScript: false })

        const errMessage = get(err, 'graphQLErrors[0].message', '')
        if (errMessage.includes('has an existing macro')) {
          setToast(
            TOAST.TYPE.WARNING,
            'Script with that name already exists. Please change the name and try again.',
            TOAST.DURATION.MEDIUM
          )
          return
        }

        setToast(
          TOAST.TYPE.ERROR,
          'Error creating Script. Please try again.',
          TOAST.DURATION.MEDIUM
        )
        console.error('Error creating script', err)
        logger.error('Error creating script via physician script editor')
      })
  };

  openSectionSelection = (blockId) => {
    this.setState({ sectionOptionsOpen: blockId })
  };

  onHandleUnidecode = (event) => {
    const { beforeText, afterText } = event

    logger.info(
      `[Unidecode Conversion] - New Script | Before: ${beforeText} | After: ${afterText} `
    )
    setToast(
      TOAST.TYPE.INFO,
      'Converted unsupported text',
      TOAST.DURATION.SHORT
    )
  };

  renderNewSectionBlock = (block, blockIdx) => {
    const { sections } = this.props
    const section = sections.find((s) => s.id === block.sectionId)
    const { sectionOptionsOpen } = this.state
    let sectionName = section?.name
    if (sectionName === 'Any Section') {
      sectionName = "Any section I'm dictating in"
    }
    return (
      <SectionBlock>
        <Platter onClick={(e) => this.openSectionSelection(block?.sectionId)}>
          <p className='label'>Insert In</p>
          <p className='section-name'>{sectionName}</p>
        </Platter>
        {sectionOptionsOpen && (
          <SectionOptions className='section-dropdown'>
            {sections.map((section, idx) => {
              let sName = section?.name
              if (sName === 'Any Section') {
                sName = "Any section I'm dictating in"
              }
              return (
                <div
                  key={idx}
                  onClick={(e) => this.onSelectSection(blockIdx, section.id, e)}
                  className='section-dropdown'
                >
                  {sName}
                </div>
              )
            })}
          </SectionOptions>
        )}
        <span data-cy='settings-addscript-editor'>
          <Editor
            defaultValue={block.content}
            persona='PHYSICIAN'
            placeholder='Enter script content'
            editorType={EDITOR_TYPE.SCRIPT}
            readOnly={false}
            onChange={(val) => this.onEditorChange(val, block.sectionId)}
            style={{
              fontSize: '10px',
              borderRadius: '4px',
              padding: '5px',
              boxShadow: '0 0 2px 0 rgba(0, 0, 0, 0.3)'
            }}
            handleUnidecode={this.onHandleUnidecode}
            enableUnidecoding
            enableChips={isOrgEmrIntegrated()}
          />
        </span>
      </SectionBlock>
    )
  };

  renderNewAmwellSectionBlock = (block, blockIdx) => {
    const { sections } = this.props
    const section = sections.find((s) => s.id === block.sectionId)
    const { sectionOptionsOpen } = this.state
    let sectionName = section?.name
    if (sectionName === 'Any Section') {
      sectionName = "Any section I'm dictating in"
    }
    return (
      <AmwellSectionBlock>
        <AmwellPlatter
          onClick={(e) => this.openSectionSelection(block?.sectionId)}
        >
          <p className='label'>Insert In</p>
          <p className='section-name'>{sectionName}</p>
        </AmwellPlatter>
        {sectionOptionsOpen && (
          <AmwellSectionOptions className='section-dropdown'>
            {sections.map((section, idx) => {
              let sName = section?.name
              if (sName === 'Any Section') {
                sName = "Any section I'm dictating in"
              }
              return (
                <AmwellScriptOption
                  key={idx}
                  onClick={(e) => this.onSelectSection(blockIdx, section.id, e)}
                  className='section-dropdown'
                >
                  {sName}
                </AmwellScriptOption>
              )
            })}
          </AmwellSectionOptions>
        )}
        <span data-cy='settings-addscript-editor'>
          <Editor
            defaultValue={block.content}
            persona='PHYSICIAN'
            placeholder='Enter script content'
            editorType={EDITOR_TYPE.SCRIPT}
            readOnly={false}
            onChange={(val) => this.onEditorChange(val, block.sectionId)}
            style={{
              borderRadius: '4px',
              padding: '16px',
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            }}
            handleUnidecode={this.onHandleUnidecode}
            enableUnidecoding
            enableChips={isOrgEmrIntegrated()}
          />
        </span>
      </AmwellSectionBlock>
    )
  };

  renderAmwellScriptEditor = () => {
    const { scriptName } = this.state
    return (
      <AmwellInvisibleContainer>
        <AmwellContainer onClick={(e) => e.stopPropagation()}>
          <AmwellBody>
            <AmwellScriptHeader>
              <AmwellScriptHeading>New Script</AmwellScriptHeading>
              <CloseModalButtonDark onClick={this.handleClose}>
                <div id='mdiv'>
                  <div className='mdiv'>
                    <div className='md' />
                  </div>
                </div>
              </CloseModalButtonDark>
            </AmwellScriptHeader>
            <AmwellContent>
              <AmwellScriptNameInput
                name='scriptName'
                value={scriptName}
                type='text'
                placeholder='Type script name'
                onChange={this.handleChange}
              />
              {this.state.blocks.map((block, blockIdx) => {
                return this.renderNewAmwellSectionBlock(block, blockIdx)
              })}
            </AmwellContent>
            <AmwellFooter>
              <AmwellActionButton onClick={this.handleCreateScript}>
                Save
              </AmwellActionButton>
            </AmwellFooter>
          </AmwellBody>
        </AmwellContainer>
      </AmwellInvisibleContainer>
    )
  };

  renderDefaultScriptEditor = () => {
    const { isCreatingScript, scriptName } = this.state
    return (
      <InvisibleContainer>
        <Container onClick={(e) => e.stopPropagation()}>
          <Body>
            <HeaderActions>
              <Button
                data-cy='settings-addscript-cancel-btn'
                onClick={this.handleClose}
              >
                Cancel
              </Button>
              <h4>Add Script</h4>
              <Button
                data-cy='settings-addscript-save-btn'
                onClick={this.handleCreateScript}
                disabled={isCreatingScript || !scriptName}
              >
                {isCreatingScript ? 'Loading' : 'Save'}
              </Button>
            </HeaderActions>
            <div className='content' style={{ width: '70%' }}>
              <ScriptNameInput
                name='scriptName'
                value={scriptName}
                type='text'
                placeholder='Type script name'
                onChange={this.handleChange}
              />
              <ScriptNameTip>
                Choose a name that is easy to say and remember
              </ScriptNameTip>
              {this.state.blocks.map((block, blockIdx) => {
                return this.renderNewSectionBlock(block, blockIdx)
              })}
            </div>
          </Body>
        </Container>
      </InvisibleContainer>
    )
  };

  render () {
    const isAmwell = isAmwellTheme()
    if (isAmwell) {
      return this.renderAmwellScriptEditor()
    }
    return this.renderDefaultScriptEditor()
  }
}

export default compose(
  graphql(CreateScript, { name: 'createScript' }),
  withApollo,
  withRouter
)(NewScriptModal)
